import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivityStatusEnum,
  CreateTimeslotBodyInterface,
  TimeslotInterface,
} from '@sport-activities/sdk';
import { of, Subscription } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';

import { VtmnButtonVariantEnum } from '~/component/buttons/vtmn-button/enum/vtmn-button-variant.enum';
import { ModalComponent } from '~/component/modal/modal.component';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { TitleService } from '~/core/services/title/title.service';
import { convertCreationTimeslotsToTimeslotBodyInterface } from '~/organizer/activity-planning/helpers/timeslots/convert-creation-timeslots-to-timeslot-body-interface/convert-creation-timeslots-to-timeslot-body-interface.helper';
import { ActivitySelectionService } from '~/organizer/activity-planning/services/activity-selection.service';
import { CalendarCreationTimeslotInterface } from '~/organizer/shared/components/shared/calendar/interfaces/recurrent-event.interface';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { ParamethorNameEnum } from '~/paramethor/names.enum';

@Component({
  selector: 'app-organizer-activity-planning-my-planning-validation',
  templateUrl: './my-planning-validation.component.html',
})
export class OrganizerActivityPlanningPageMyPlanningValidationComponent
  implements OnInit, OnDestroy
{
  @ViewChild(ModalComponent) public warningModal: ModalComponent;

  public readonly vtmnButtonVariant = VtmnButtonVariantEnum;

  private isLoading = false;
  private timeslotBodiesToCreate: CreateTimeslotBodyInterface[] = [];
  private timeslotsSubscription$: Subscription;

  public creationTimeslots: CalendarCreationTimeslotInterface<Date>[] = [];
  public existingTimeslots: TimeslotInterface[] = [];
  public nextLabel = '';
  public totalCreatedTimeslots = 0;
  public totalTimeslotToCreate = 0;

  constructor(
    private readonly activitySelectionService: ActivitySelectionService,
    private readonly organizerSharedActivityService: OrganizerSharedActivityService,
    private readonly organizerSharedOrganizationService: OrganizerSharedOrganizationService,
    private readonly redirectionService: RedirectionService,
    private readonly titleService: TitleService,
    private readonly paramethorService: ParamethorService,
    public readonly layoutElementRef: ElementRef,
    public readonly translateService: TranslateService
  ) {}

  public ngOnDestroy(): void {
    if (this.timeslotsSubscription$) {
      this.timeslotsSubscription$.unsubscribe();
    }
  }

  public ngOnInit(): void {
    this.titleService.setPageTitle(
      'seo.organizer.activity_planning.validation.title'
    );

    this.creationTimeslots =
      this.activitySelectionService.getCreatedTimeslots();
    this.existingTimeslots =
      this.activitySelectionService.getExistingTimeslots();

    if (!this.existingTimeslots?.length) {
      this.timeslotsSubscription$ = this.organizerSharedActivityService
        .getTimeslotsByOrganizationId(
          this.organizerSharedOrganizationService.organization.identifier
        )
        .subscribe((timeslots) => {
          this.existingTimeslots = timeslots;
          this.activitySelectionService.setExistingTimeslots(
            this.existingTimeslots
          );
        });
    }

    const { sku } = this.organizerSharedActivityService.activity;

    this.timeslotBodiesToCreate =
      convertCreationTimeslotsToTimeslotBodyInterface(
        this.creationTimeslots,
        this.translateService.currentLang,
        this.paramethorService.get(ParamethorNameEnum.DefaultPaidOfferName),
        `${this.paramethorService.get(ParamethorNameEnum.Currency, 'EUR')}`,
        this.activitySelectionService.getOverloads(sku)
      );

    this.totalTimeslotToCreate = this.timeslotBodiesToCreate?.length;
  }

  public get isNextDisabled(): boolean {
    return this.isLoading || this.totalTimeslotToCreate === 0;
  }

  public redirectOnSuccess(): Promise<boolean> {
    this.warningModal.hide();
    this.activitySelectionService.resetCreatedTimeslots();

    return this.redirectionService.redirect(
      'organizer.activity-sharing.steps-3',
      {
        queryParams: {
          activityId: this.organizerSharedActivityService.activity.sku,
        },
      }
    );
  }

  public submit(): void {
    this.isLoading = true;
    this.organizerSharedActivityService
      .postTimeslotsBulk(this.timeslotBodiesToCreate)
      .pipe(
        mergeMap(() => {
          const { sku, status } = this.organizerSharedActivityService.activity;

          if (status === ('PUBLISHED' as ActivityStatusEnum)) {
            return of(null);
          }

          return this.organizerSharedActivityService
            .postActivityAction(sku, 'publish')
            .pipe(
              tap(() => {
                // prevent a potential 403 error if an organizer publish multiple timeslots
                // without reloading page ...
                this.organizerSharedActivityService.activity.status =
                  'PUBLISHED' as ActivityStatusEnum;
              })
            );
        })
      )
      .subscribe({
        next: () => {
          this.warningModal.show();
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }
}
